import {
  RecentAddressOrder,
  RecentAddressOrderItem,
  RecentOrderI18nText,
  RecentOrderStatus,
} from '../types/order';

export type OrderStatusTone = 'info' | 'success' | 'warning' | 'danger';

export interface OrderStatusDisplay {
  labelKey: string;
  descriptionKey: string;
  tone: OrderStatusTone;
}

export interface OrderItemsSummary {
  visibleItems: string[];
  hiddenItemCount: number;
  totalQuantity: number;
}

type OrderLanguage = keyof RecentOrderI18nText;

const STATUS_STEP_INDEX: Partial<Record<RecentOrderStatus, number>> = {
  PAID: 0,
  BROKER_ACCEPTED: 1,
  COOKING: 2,
  DELIVERY_COMPLETED: 3,
};

const HOME_AFFORDANCE_ACTIVE_STATUSES = new Set<RecentOrderStatus>([
  'PAID',
  'BROKER_ACCEPTED',
  'COOKING',
]);

const STATUS_DISPLAY: Record<RecentOrderStatus, OrderStatusDisplay> = {
  PAID: {
    labelKey: 'orders.status.paid',
    descriptionKey: 'orders.statusDescription.paid',
    tone: 'info',
  },
  BROKER_ACCEPTED: {
    labelKey: 'orders.status.accepted',
    descriptionKey: 'orders.statusDescription.accepted',
    tone: 'info',
  },
  COOKING: {
    labelKey: 'orders.status.cooking',
    descriptionKey: 'orders.statusDescription.cooking',
    tone: 'warning',
  },
  DELIVERY_COMPLETED: {
    labelKey: 'orders.status.delivered',
    descriptionKey: 'orders.statusDescription.delivered',
    tone: 'success',
  },
  CANCELLED: {
    labelKey: 'orders.status.cancelled',
    descriptionKey: 'orders.statusDescription.cancelled',
    tone: 'danger',
  },
};

export const getOrderStatusStep = (status: RecentOrderStatus): number | null => {
  return STATUS_STEP_INDEX[status] ?? null;
};

export const getOrderStatusDisplay = (status: RecentOrderStatus): OrderStatusDisplay => {
  return STATUS_DISPLAY[status];
};

export const getOrderTimeLabelKey = (status: RecentOrderStatus): string => {
  return status === 'DELIVERY_COMPLETED' ? 'orders.deliveryCompletedAt' : 'orders.expectedDelivery';
};

const LANGUAGE_TO_I18N_KEY: Record<string, OrderLanguage> = {
  ko: 'ko',
  en: 'en',
  ja: 'ja',
  'zh-CN': 'zhCn',
  'zh-TW': 'zhTw',
};

export const getLocalizedOrderText = (
  fallback: string,
  i18nText: RecentOrderI18nText | undefined,
  language: string,
): string => {
  const i18nKey = LANGUAGE_TO_I18N_KEY[language] ?? 'en';
  const localizedText = i18nText?.[i18nKey]?.trim();

  return localizedText || fallback;
};

export const summarizeOrderItems = (
  items: RecentAddressOrderItem[],
  visibleCount = 2,
  language = navigator.language,
): OrderItemsSummary => {
  const visibleItems = items
    .slice(0, visibleCount)
    .map((item) => `${getLocalizedOrderText(item.name, item.nameI18n, language)} x${item.quantity}`);
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

  return {
    visibleItems,
    hiddenItemCount: Math.max(items.length - visibleCount, 0),
    totalQuantity,
  };
};

export const formatOrderOptions = (item: RecentAddressOrderItem): string | null => {
  const optionTexts = item.options
    .map((option) => {
      const optionName = option.optionName.trim();
      const optionItems = option.optionItems.map((optionItem) => optionItem.trim()).filter(Boolean);

      if (!optionName && optionItems.length === 0) {
        return null;
      }

      if (!optionName) {
        return optionItems.join(', ');
      }

      if (optionItems.length === 0) {
        return optionName;
      }

      return `${optionName}: ${optionItems.join(', ')}`;
    })
    .filter((optionText): optionText is string => Boolean(optionText));

  return optionTexts.length > 0 ? optionTexts.join(' · ') : null;
};

const normalizeOrderDateTimeForKst = (dateTime: string): string => {
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(dateTime);
  return hasTimezone ? dateTime : `${dateTime}+09:00`;
};

const formatTimeOnlyInKst = (date: Date, fallback: string, locale: string): string => {
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  const formattedTime = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Seoul',
  }).format(date);

  return `${formattedTime} KST`;
};

export const formatOrderTime = (dateTime: string, locale = navigator.language): string => {
  const date = new Date(normalizeOrderDateTimeForKst(dateTime));

  if (Number.isNaN(date.getTime())) {
    return dateTime;
  }

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Seoul',
  }).format(date);
};

export const formatOrderTimeOnly = (dateTime: string, locale = navigator.language): string => {
  const date = new Date(normalizeOrderDateTimeForKst(dateTime));

  return formatTimeOnlyInKst(date, dateTime, locale);
};

export const getHomeOrderAffordanceName = (order: RecentAddressOrder): string => {
  return (
    order.franchiseNameI18n?.en?.trim() ||
    order.franchiseName?.trim() ||
    order.restaurantNameI18n?.en?.trim() ||
    order.restaurantName?.trim() ||
    ''
  );
};

export const getRemainingDeliveryMinutes = (
  expectedDeliveryCompletedAt: string,
  now = new Date(),
): number | null => {
  const expectedDate = new Date(normalizeOrderDateTimeForKst(expectedDeliveryCompletedAt));

  if (Number.isNaN(expectedDate.getTime()) || Number.isNaN(now.getTime())) {
    return null;
  }

  return Math.ceil((expectedDate.getTime() - now.getTime()) / 60000);
};

export const selectHomeOrderAffordanceOrder = (
  orders: RecentAddressOrder[],
  now = new Date(),
): RecentAddressOrder | null => {
  const activeOrders = orders
    .filter((order) => HOME_AFFORDANCE_ACTIVE_STATUSES.has(order.status))
    .filter((order) => {
      if (!order.expectedDeliveryCompletedAt) {
        return false;
      }

      const remainingMinutes = getRemainingDeliveryMinutes(order.expectedDeliveryCompletedAt, now);
      return remainingMinutes !== null && remainingMinutes > 0;
    });

  if (activeOrders.length === 0) {
    return null;
  }

  return [...activeOrders].sort((a, b) => {
    const remainingA = getRemainingDeliveryMinutes(a.expectedDeliveryCompletedAt || '', now) ?? Number.MAX_SAFE_INTEGER;
    const remainingB = getRemainingDeliveryMinutes(b.expectedDeliveryCompletedAt || '', now) ?? Number.MAX_SAFE_INTEGER;

    return remainingA - remainingB;
  })[0];
};
